import type { Match } from "@/lib/types";

/** Resolved logo URLs for the two sides of a match. `null` means
 * "no logo available — fall back to the initial-letter chip". */
export type SideLogos = {
  homeUrl: string | null;
  awayUrl: string | null;
};

type LogoSourceMatch = Pick<
  Match,
  "id" | "team_id" | "home_team_id" | "away_team_id" | "home_logo" | "away_logo" | "opponent_logo"
>;

/** Decide which side gets the club logo (always the tenant team) and
 * which side gets the opponent's per-match upload.
 *
 * Single-club app today: the install carries one canonical
 * `club_logo_url` in settings. The tenant side of a match — whichever
 * side has `team_id === match.team_id` — always renders that. The
 * other side falls back to `match.opponent_logo` (the new per-match
 * upload) or the legacy `home_logo` / `away_logo` files for matches
 * that predate the migration. For "Not on this team" friendlies
 * where neither side is the tenant, the opponent slot is treated as
 * the away side (matches the legacy renderer's behavior) and the
 * home slot falls back to any legacy `home_logo`.
 *
 * Cache-busting is the caller's responsibility — append `?v=…` to
 * the returned URL when you've just uploaded a replacement and need
 * the browser to drop the old cached image.
 */
export function getSideLogos(
  match: LogoSourceMatch | null | undefined,
  clubLogoUrl: string | null | undefined,
): SideLogos {
  if (!match) return { homeUrl: null, awayUrl: null };
  const tenantId = match.team_id ?? null;
  const homeIsTenant = !!tenantId && match.home_team_id === tenantId;
  const awayIsTenant = !!tenantId && match.away_team_id === tenantId;
  const opponentEndpoint = match.opponent_logo
    ? `/api/matches/${match.id}/opponent-logo`
    : null;
  const legacyHome = match.home_logo
    ? `/api/matches/${match.id}/logo/home`
    : null;
  const legacyAway = match.away_logo
    ? `/api/matches/${match.id}/logo/away`
    : null;

  // Tenant side → club logo. Other side → opponent (with legacy fallback).
  if (homeIsTenant) {
    return {
      homeUrl: clubLogoUrl || null,
      awayUrl: opponentEndpoint ?? legacyAway,
    };
  }
  if (awayIsTenant) {
    return {
      homeUrl: opponentEndpoint ?? legacyHome,
      awayUrl: clubLogoUrl || null,
    };
  }
  // Friendly with no tenant team — fall back to legacy per-side files,
  // letting opponent_logo populate the away slot if it's the only thing
  // we have.
  return {
    homeUrl: legacyHome,
    awayUrl: opponentEndpoint ?? legacyAway,
  };
}

/** Returns true when, for this match, the operator's per-match upload
 * controls the HOME side (i.e. the tenant team is the AWAY side). Used
 * by MatchEditModal to label the existing-logo preview correctly. */
export function opponentIsHomeSide(match: LogoSourceMatch | null | undefined): boolean {
  if (!match) return false;
  const tenantId = match.team_id ?? null;
  return !!tenantId && match.away_team_id === tenantId;
}
